///
/// EarthBrowser
///
/// Created by Matt Giger on 11/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// WebGL context loss. Nothing in the engine rebuilds its GPU resources
/// after a loss, so a lost context is a dead canvas: on desktop after a
/// GPU reset or driver update, on mobile when the browser reclaims GPU
/// memory (occasionally fired shortly before a tab kill). Rather than
/// leave the globe frozen black, the guard turns the event into a
/// controlled reload that the crash sentinel treats as a crash of this
/// tab, so the heavy layers come back staggered (CrashRecovery level 1;
/// repeated losses escalate like any other crash loop).
///
/// A loss while the page is hidden is routine housekeeping by the browser,
/// not a sign anything was too big: the reload waits for the page to come
/// back and is not counted as a crash.

import { reloadAsCrash, recordCrumb } from "@/earthbrowser/utils/CrashSentinel"

export const cContextLostReason = "webgl-context-lost"

/** Watch `canvas` for context loss. Returns a function that stops watching */
export function installContextLossGuard(canvas: HTMLCanvasElement): () => void {
	let handled = false

	const onLost = (event: Event) => {
		if (handled) return
		handled = true
		// Opt into a possible restore even though we reload: it keeps the
		// browser from treating the context as permanently gone meanwhile
		event.preventDefault()

		if (document.visibilityState === "hidden") {
			recordCrumb("webgl:context-lost hidden")
			console.warn("[ContextLossGuard] WebGL context lost while hidden; reloading when visible")
			const onVisible = () => {
				if (document.visibilityState !== "visible") return
				document.removeEventListener("visibilitychange", onVisible)
				window.location.reload()
			}
			document.addEventListener("visibilitychange", onVisible)
			return
		}

		console.warn("[ContextLossGuard] WebGL context lost; reloading with crash recovery")
		reloadAsCrash(cContextLostReason)
	}

	canvas.addEventListener("webglcontextlost", onLost)
	return () => {
		canvas.removeEventListener("webglcontextlost", onLost)
	}
}
